'use client';

import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { getFormatLocale } from '../lib/useNationalEnvironment';
import { useEscapeClose } from '../lib/useEscapeClose';
import { useExitAnimation } from '../lib/useExitAnimation';

type AuditLogEntry = {
  id: number;
  entityType: string;
  entityId: number;
  action: string;
  changes: string | null;
  userId: number | null;
  username: string | null;
  timestamp: string;
};

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'bg-moss/20 text-moss-dark border-moss/30',
  UPDATE: 'bg-sky/20 text-sky-dark border-sky/30',
  DELETE: 'bg-rust/20 text-rust-dark border-rust/30',
  MERGE: 'bg-amber-100 text-amber-800 border-amber-300',
  POHODA_WRITE: 'bg-moss/20 text-moss-dark border-moss/30',
};

const ACTION_ICONS: Record<string, string> = {
  CREATE: '✨',
  UPDATE: '✏️',
  DELETE: '🗑️',
  MERGE: '🔀',
  POHODA_WRITE: '💶',
};

const ACTION_LABEL_KEYS: Record<string, string> = {
  POHODA_WRITE: 'auditActionPohodaWrite',
};

type HistoryEntryProps = {
  entry: AuditLogEntry;
  byUserLabel: string;
  systemLabel: string;
};

function HistoryEntry({ entry, byUserLabel, systemLabel }: HistoryEntryProps) {
  const { t } = useTranslation();
  let changesObj: Record<string, { old: string; new: string }> | string | null = null;
  try {
    if (entry.changes) changesObj = JSON.parse(entry.changes);
  } catch {
    changesObj = entry.changes;
  }
  const isSystem = entry.username === 'system';
  return (
    <div className="flex gap-2 text-xs">
      <div className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full border ${isSystem ? 'bg-wheat/20 text-wood border-wheat/60' : (ACTION_COLORS[entry.action] || 'bg-wheat/30 text-wood border-wheat')}`}>
        {isSystem ? '⚙' : (ACTION_ICONS[entry.action] || '•')}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-bark">{ACTION_LABEL_KEYS[entry.action] ? t(ACTION_LABEL_KEYS[entry.action]) : entry.action}</span>
          <span className="text-wood">
            {new Date(entry.timestamp).toLocaleString(getFormatLocale())}
          </span>
          {isSystem && (
            <span className="px-1.5 py-0.5 rounded bg-wheat/30 text-wood text-[11px] font-medium">⚙ {systemLabel}</span>
          )}
        </div>
        {entry.username && !isSystem && (
          <div className="text-wood">{byUserLabel}: {entry.username}</div>
        )}
        {changesObj && typeof changesObj === 'object' && (
          <div className="mt-1 space-y-0.5">
            {Object.entries(changesObj).map(([field, vals]) => (
              <div key={field} className="text-wood">
                <span className="font-medium">{field}:</span>{' '}
                <span className="line-through opacity-60">{String(vals.old)}</span>{' '}
                → <span className="text-bark font-medium">{String(vals.new)}</span>
              </div>
            ))}
          </div>
        )}
        {changesObj && typeof changesObj === 'string' && (
          <div className="mt-1 text-wood break-all">{changesObj}</div>
        )}
      </div>
    </div>
  );
}

type EntityHistoryPanelProps = {
  entityType: string;
  entityId: number;
  className?: string;
};

export default function EntityHistoryPanel({ entityType, entityId, className }: EntityHistoryPanelProps) {
  const { t } = useTranslation();
  const [history, setHistory] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [showFull, setShowFull] = useState(false);

  useEffect(() => {
    if (!entityId) return;
    setLoading(true);
    apiFetch<AuditLogEntry[]>(`/api/user/entity-audit/${entityType}/${entityId}`)
      .then(data => {
        setHistory(data);
        setError(null);
      })
      .catch(err => {
        setError(err.message || 'Failed to load history');
      })
      .finally(() => setLoading(false));
  }, [entityType, entityId]);

  useEscapeClose(showFull, () => setShowFull(false));
  const showFullExiting = useExitAnimation(showFull);

  if (loading) {
    return (
      <div className={`rounded-rustic border border-wheat bg-cream p-3 ${className || ''}`}>
        <div className="text-sm text-wood animate-pulse">{t('loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-rustic border border-wheat bg-cream p-3 ${className || ''}`}>
        <div className="text-sm text-rust-dark">{error}</div>
      </div>
    );
  }

  return (
    <>
      <div className={`rounded-rustic border border-wheat bg-cream ${className || ''}`}>
        <div className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-bark">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 hover:bg-wheat/20 transition rounded px-1 -mx-1"
          >
            <span>📋</span>
            <span>{t('entityHistory')}</span>
            <span className="text-xs text-wood">({history.length})</span>
            <span className="text-wood">{expanded ? '▲' : '▼'}</span>
          </button>
          <button
            onClick={() => setShowFull(true)}
            title={t('entityHistoryFullView')}
            className="p-1 text-wood hover:text-bark hover:bg-wheat/30 rounded transition text-xs"
          >
            ⛶
          </button>
        </div>
        {expanded && (
          <div className="border-t border-wheat px-3 py-2 space-y-2 max-h-64 overflow-y-auto">
            {history.length === 0 ? (
              <div className="text-xs text-wood italic py-2 text-center">{t('entityHistoryEmpty')}</div>
            ) : (
              history.map(entry => (
                <HistoryEntry key={entry.id} entry={entry} byUserLabel={t('byUser')} systemLabel={t('bySystem')} />
              ))
            )}
          </div>
        )}
      </div>

      {(showFull || showFullExiting) && (
        <div
          className={`fixed inset-0 z-[10000] flex items-center justify-center bg-bark/40 backdrop-blur-sm overflow-y-auto ${showFull ? 'animate-backdropIn' : 'animate-backdropOut'}`}
          onClick={(e) => { if (e.target === e.currentTarget) setShowFull(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            className={`bg-linen border border-wheat rounded-rustic shadow-xl w-full max-w-3xl mx-4 my-8 flex flex-col max-h-[90vh] ${showFull ? 'animate-modalIn' : 'animate-modalOut'}`}
          >
            <div className="flex items-center justify-between border-b border-wheat px-4 py-3">
              <h3 className="text-sm font-semibold text-bark flex items-center gap-2">
                <span>📋</span>
                <span>{t('entityHistory')}</span>
                <span className="text-xs text-wood">({history.length})</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowFull(false)}
                className="text-wood hover:text-bark hover:bg-wheat/30 rounded px-2 py-1 transition text-sm"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto px-4 py-3 space-y-3">
              {history.length === 0 ? (
                <div className="text-sm text-wood italic py-4 text-center">{t('entityHistoryEmpty')}</div>
              ) : (
                history.map(entry => (
                  <HistoryEntry key={entry.id} entry={entry} byUserLabel={t('byUser')} systemLabel={t('bySystem')} />
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
